import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import "./vision.css";

const mnd = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const dato = (iso) => {
  if (!iso || iso.length < 10) return "April 2027";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${mnd[m - 1]} ${y}`;
};
const nokFmt = (v) => `NOK ${new Intl.NumberFormat("en-US").format(Math.round(v || 0))}`;

const LABELS = {
  "01": "Prepared for", "02": "Why this partnership", "03": "The audience",
  "04": "Recommended partnership", "05": "What it includes", "06": "Activation concept",
  "07": "Commercial terms", "08": "Next step",
};
const KAPITLER = Object.keys(LABELS);

const deliverableLines = (d, extras) => {
  const ut = [];
  if (d.physicalTickets > 0) ut.push(`${d.physicalTickets} tickets — guaranteed seats in the room`);
  if (d.dinnerSeats > 0) ut.push(`${d.dinnerSeats} seats at the Summit Dinner`);
  if (d.expoActivation) ut.push("Curated activation in the AI Experience");
  if (d.categoryExclusivity) ut.push("Category exclusivity");
  if (d.broadcastVisibility === "premium") ut.push("Premium broadcast visibility — live stream and all session content");
  else if (d.broadcastVisibility === "standard") ut.push("Broadcast visibility");
  if (d.executiveRoundtable) ut.push("Executive roundtable hosting");
  [d.brandPresence, d.hospitality, d.contentRights].filter(Boolean).forEach((t) => ut.push(t));
  return [...ut, ...(extras || [])];
};

const Chap = ({ id, bilde, children, refCb }) => (
  <section id={id} ref={refCb} className={`vis-chap ${bilde ? "har-bilde" : ""}`} data-testid={`prop-chap-${id}`}>
    {bilde && <img src={bilde} alt="" className="vis-bg" />}
    {bilde && <div className="vis-bg-shade" />}
    <div className="vis-grain" />
    <div className="vis-inner">{children}</div>
  </section>
);

const Kicker = ({ nr, tx }) => (
  <div className="vis-kicker vis-fade">
    <span className="nr">{nr}</span>
    <span className="tx">{tx}</span>
  </div>
);

export default function ProposalPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: "laster" });
  const [aktiv, setAktiv] = useState("01");
  const aktivRef = useRef("01");
  const scrollRef = useRef(null);
  const chapsRef = useRef({});

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    document.title = "Partnership Proposal — Norwegian AI Summit 2027";
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    let ok = true;
    api.get(`/proposals/${token}`)
      .then(({ data }) => ok && setState({ status: "ok", d: data }))
      .catch(() => ok && setState({ status: "borte" }));
    return () => { ok = false; };
  }, [token]);

  useEffect(() => {
    if (state.status !== "ok") return undefined;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          aktivRef.current = e.target.id;
          setAktiv(e.target.id);
        }
      }),
      { root: scrollRef.current, threshold: 0.45 }
    );
    Object.values(chapsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [state.status]);

  useEffect(() => {
    if (state.status !== "ok") return undefined;
    const key = (e) => {
      const t = e.target?.tagName;
      if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT") return;
      const frem = ["ArrowDown", "ArrowRight", "PageDown"].includes(e.key) || (e.key === " " && t !== "BUTTON");
      const bak = ["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key);
      if (!frem && !bak && e.key !== "Home" && e.key !== "End") return;
      e.preventDefault();
      const idx = KAPITLER.indexOf(aktivRef.current);
      let neste = idx;
      if (frem) neste = Math.min(KAPITLER.length - 1, idx + 1);
      else if (bak) neste = Math.max(0, idx - 1);
      else if (e.key === "Home") neste = 0;
      else if (e.key === "End") neste = KAPITLER.length - 1;
      chapsRef.current[KAPITLER[neste]]?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [state.status]);

  const reg = (id) => (el) => { chapsRef.current[id] = el; };
  const gaa = (id) => chapsRef.current[id]?.scrollIntoView({ behavior: "smooth" });

  if (state.status === "laster") {
    return (
      <div className="vis-root vis-borte" data-testid="proposal-laster">
        <p style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase", color: "#8b9199" }}>
          Norwegian AI Summit
        </p>
      </div>
    );
  }
  if (state.status === "borte") {
    return (
      <div className="vis-root vis-borte" data-testid="proposal-borte">
        <p className="vis-brand">Norwegian AI Summit <span className="gul">2027</span></p>
        <h1 style={{ fontFamily: '"Switzer", sans-serif', fontSize: "clamp(26px, 4vw, 44px)", fontWeight: 500, color: "#f5f1e8" }}>
          This proposal link is no longer active.
        </h1>
        <p style={{ fontSize: 14, color: "#8b9199" }}>Please reach out to your contact for an updated proposal.</p>
      </div>
    );
  }

  const d = state.d;
  const rec = d.recommended;
  const linjer = deliverableLines(rec.deliverables || {}, rec.extras);
  const evDato = dato(d.event?.eventDate);
  const kap = d.event?.capacity ? `~${d.event.capacity}` : "~700";
  const speakers = d.speakers || [];
  const whyAvsnitt = (d.whyText || "").split(/\n\n+/).filter(Boolean);
  const aktAvsnitt = (d.activationText || "").split(/\n\n+/).filter(Boolean);

  return (
    <div data-testid="proposal-page">
      <div className="vis-topbar">
        <span className="vis-brand">Norwegian AI Summit <span className="gul">2027</span></span>
        <span className="vis-tag" style={{ marginLeft: "auto" }} data-testid="proposal-tag">
          Commercial proposal · Prepared for {d.preparedFor} · Private &amp; confidential
        </span>
      </div>

      <div className="vis-root" data-testid="proposal-deck">
        <div className="vis-progress" data-testid="prop-progress">
          <span style={{ width: `${((KAPITLER.indexOf(aktiv) + 1) / KAPITLER.length) * 100}%` }} />
        </div>
        <div className="vis-logo" data-testid="prop-logo">
          <span className="wordmark">
            <span className="wordmark__bold">Norwegian</span>
            <span className="wordmark__light">AI Summit</span>
          </span>
        </div>
        <nav className="vis-rail" data-testid="prop-rail">
          {KAPITLER.map((k) => (
            <button key={k} type="button" onClick={() => gaa(k)} className={aktiv === k ? "on" : ""} data-label={LABELS[k]} data-testid={`prop-rail-${k}`}>
              {k}
            </button>
          ))}
        </nav>
        <div className="vis-teller" data-testid="prop-teller">
          <span className="nr">{aktiv}</span> / 08 — {LABELS[aktiv]}
        </div>

        <div className="vis-scroll" ref={scrollRef}>
          {/* 01 — PREPARED FOR */}
          <Chap id="01" bilde="/images/vision-hero-v2.jpg" refCb={reg("01")}>
            <p className="vis-brand vis-fade" style={{ fontSize: 13, letterSpacing: "0.24em" }}>
              Norwegian AI Summit <span className="gul">2027</span>
            </p>
            <p className="vis-calibre-note vis-fade" style={{ marginTop: 30 }}>Commercial proposal · Private &amp; confidential</p>
            <h1 className="vis-h vis-fade" style={{ marginTop: 14 }} data-testid="proposal-prepared-for">
              Prepared for<br />{d.preparedFor}
            </h1>
            <div className="vis-meta vis-fade">
              <span>{evDato}</span>
              <span className="dot">·</span>
              <span>Grieghallen, Bergen</span>
              <span className="dot">·</span>
              <span>{d.preparedAt}</span>
            </div>
          </Chap>

          {/* 02 — WHY THIS PARTNERSHIP */}
          <Chap id="02" refCb={reg("02")}>
            <Kicker nr="02" tx="Why this partnership" />
            <h2 className="vis-h vis-h-md vis-fade">Not sponsorship.<br />A <span className="gul">position</span>.</h2>
            {whyAvsnitt.map((t, i) => (
              <p key={i} className="vis-body vis-fade" data-testid={`proposal-why-${i}`}>{t}</p>
            ))}
          </Chap>

          {/* 03 — THE AUDIENCE */}
          <Chap id="03" bilde="/images/vision-room-v2.jpg" refCb={reg("03")}>
            <Kicker nr="03" tx="The audience" />
            <h2 className="vis-h vis-h-md vis-fade">One day. {kap} people.<br />Deliberately limited.</h2>
            <div className="vis-words vis-fade">
              {["Founders", "CEOs", "Investors", "Researchers", "Engineers", "Decision-makers"].map((w) => (
                <span key={w}><i className="pk" />{w}</span>
              ))}
            </div>
            {speakers.length > 0 && (
              <>
                <p className="vis-calibre-note vis-fade" style={{ marginTop: 34 }}>On stage — confirmed</p>
                <div className="vis-chips vis-fade" style={{ marginTop: 12 }} data-testid="proposal-speakers">
                  {speakers.slice(0, 8).map((s) => (
                    <span key={s.id} className="vis-chip">
                      {s.name}
                      <small>{[s.title, s.org].filter(Boolean).join(", ")}</small>
                    </span>
                  ))}
                </div>
              </>
            )}
          </Chap>

          {/* 04 — RECOMMENDED PARTNERSHIP */}
          <Chap id="04" refCb={reg("04")}>
            <Kicker nr="04" tx="Recommended partnership" />
            <h2 className="vis-h vis-fade" data-testid="proposal-recommended-navn">{rec.name}</h2>
            <p className="vis-price vis-fade" data-testid="proposal-recommended-pris">
              {nokFmt(rec.price)} <small>excl. VAT</small>
            </p>
            {rec.blurb && <p className="vis-body vis-fade">{rec.blurb}</p>}
            {d.alternative && (
              <p className="vis-alt-line vis-fade" data-testid="proposal-alternativ">
                Alternative: {d.alternative.name} — {nokFmt(d.alternative.price)} excl. VAT
              </p>
            )}
          </Chap>

          {/* 05 — WHAT IT INCLUDES */}
          <Chap id="05" refCb={reg("05")}>
            <Kicker nr="05" tx="What it includes" />
            <h2 className="vis-h vis-h-md vis-fade">Concrete rights.<br />Real allocations.</h2>
            <div className="vis-check vis-fade" data-testid="proposal-leveranser">
              {linjer.map((t) => (
                <div key={t}><span className="st" /><p className="t">{t}</p></div>
              ))}
            </div>
            <p className="vis-calibre-note vis-fade" data-testid="proposal-editorial-note">
              The main stage is editorially curated — speaking slots are never part of a commercial package
            </p>
          </Chap>

          {/* 06 — ACTIVATION CONCEPT */}
          <Chap id="06" bilde="/images/vision-robots.jpg" refCb={reg("06")}>
            <Kicker nr="06" tx="Activation concept" />
            <h2 className="vis-h vis-h-md vis-fade">What {d.preparedFor} can<br /><span className="gul">do</span> — not just show.</h2>
            {aktAvsnitt.map((t, i) => (
              <p key={i} className="vis-body vis-fade" data-testid={`proposal-aktivering-${i}`}>{t}</p>
            ))}
          </Chap>

          {/* 07 — COMMERCIAL TERMS */}
          <Chap id="07" refCb={reg("07")}>
            <Kicker nr="07" tx="Commercial terms" />
            <h2 className="vis-h vis-h-md vis-fade">Clear terms.<br />No surprises.</h2>
            <div className="vis-facts vis-fade" data-testid="proposal-vilkaar">
              <div><p className="k">Investment</p><p className="v">{nokFmt(rec.price)} excl. VAT</p></div>
              <div><p className="k">Payment plan</p><p className="v">{d.paymentPlanText || "By agreement"}</p></div>
              <div><p className="k">Valid until</p><p className="v">{d.validUntil ? dato(d.validUntil) : "By agreement"}</p></div>
              <div><p className="k">Scope</p><p className="v">Norwegian AI Summit 2027 · {evDato}</p></div>
            </div>
          </Chap>

          {/* 08 — NEXT STEP */}
          <Chap id="08" refCb={reg("08")}>
            <Kicker nr="08" tx="Next step" />
            <h2 className="vis-h vis-fade">Let's build it<br /><span className="gul">together</span>.</h2>
            <p className="vis-body vis-fade">
              One conversation is enough to get started. We'll walk through the partnership,
              tailor the activation and agree on the details.
            </p>
            <div className="vis-kontakt vis-fade">
              <a href={`mailto:${d.contactEmail}`} data-testid="proposal-kontakt-epost">{d.contactEmail}</a>
              <p>{d.contactName} · Norwegian AI Summit · Prepared for {d.preparedFor} — private &amp; confidential, please don't distribute.</p>
            </div>
          </Chap>
        </div>
      </div>
    </div>
  );
}
